import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { siteInspections } from '../api';
import AdminLayout from '../layouts/AdminLayout';
import {
  FaArrowLeft, FaSpinner, FaCheckCircle, FaFilePdf,
  FaEnvelope, FaWhatsapp, FaExclamationCircle, FaClipboardCheck
} from 'react-icons/fa';

// ============================================
// SITE INSPECTION DETAIL VIEW
// Full readable report of a submitted site inspection,
// with Print/Download (PDF), Email and WhatsApp options.
// Route: /site-inspection/:token (admin protected)
// ============================================
function SiteInspectionDetail({ onLogout }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [questions, setQuestions] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchInspection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInspection = async () => {
    setLoading(true);
    try {
      const data = await siteInspections.getByToken(token);
      if (!data.success) {
        setError(data.error || 'Inspection not found');
        return;
      }
      setInspection(data.inspection);
      setQuestions(data.questions || []);
    } catch (err) {
      console.error('Error fetching inspection:', err);
      setError('Failed to load inspection');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const buildSummaryText = () => {
    const q = inspection?.quote || {};
    let text = `SITE INSPECTION REPORT\n`;
    text += `================================\n`;
    text += `Quote: ${q.quote_number || 'N/A'}\n`;
    text += `Customer: ${q.customer_name || 'N/A'}\n`;
    text += `Type: ${q.quote_type || 'N/A'}\n`;
    text += `Submitted: ${formatDate(inspection?.submitted_at)}\n`;
    text += `--------------------------------\n\n`;
    questions.forEach((question) => {
      const answer = inspection?.answers?.[question.key] || '—';
      text += `${question.label}\n${answer}\n\n`;
    });
    if (inspection?.custom_questions && inspection.custom_questions.length > 0) {
      text += `--- Custom Questions ---\n\n`;
      inspection.custom_questions.forEach((cq) => {
        const answer = inspection.answers?.[cq.key] || '—';
        text += `${cq.label}\n${answer}\n\n`;
      });
    }
    return text;
  };

  const handleEmail = () => {
    const q = inspection?.quote || {};
    const subject = encodeURIComponent(`Site Inspection Report — ${q.quote_number || ''}`);
    const body = encodeURIComponent(buildSummaryText());
    window.location.href = `mailto:${q.contact_email || ''}?subject=${subject}&body=${body}`;
  };

  const handleWhatsApp = () => {
    const q = inspection?.quote || {};
    const digits = (q.contact_phone || '').replace(/\D/g, '');
    if (!digits) {
      toast.error('No customer phone number on this quote.');
      return;
    }
    const wa = digits.startsWith('27') ? digits : digits.length === 9 ? `27${digits}` : digits.length === 10 && digits.startsWith('0') ? `27${digits.substring(1)}` : digits;
    const msg = encodeURIComponent(buildSummaryText());
    window.open(`https://wa.me/${wa}?text=${msg}`, '_blank');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading inspection...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !inspection) {
    return (
      <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
          <FaExclamationCircle className="text-5xl text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-600 mb-2">Inspection Not Found</h2>
          <p className="text-gray-500">{error || 'The inspection does not exist.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 inline-flex items-center gap-2 bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors"
          >
            <FaArrowLeft size={14} /> Back
          </button>
        </div>
      </AdminLayout>
    );
  }

  const quote = inspection.quote || {};
  const answers = inspection.answers || {};
  const isSubmitted = inspection.status === 'submitted';

  return (
    <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
      {/* Print styles: when printing, hide ALL dashboard chrome and show only the report */}
      <style>{`
        @media print {
          body { background: white !important; }
          body * { visibility: hidden !important; }
          #site-inspection-print-area, #site-inspection-print-area * { visibility: visible !important; }
          #site-inspection-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 20px !important;
            border: none !important;
            box-shadow: none !important;
          }
          aside, nav, header, footer { display: none !important; }
        }
      `}</style>

      {/* Header (hidden when printing) */}
      <div className="flex flex-wrap items-center gap-4 mb-6 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-navy"
        >
          <FaArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-navy">📋 Site Inspection Report</h1>
          <p className="text-sm text-gray-500">
            Quote {quote.quote_number} • {quote.customer_name}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors"
          >
            <FaFilePdf size={14} /> Download / Print
          </button>
          <button
            onClick={handleEmail}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <FaEnvelope size={14} /> Send by Email
          </button>
          <button
            onClick={handleWhatsApp}
            className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green transition-colors"
          >
            <FaWhatsapp size={14} /> WhatsApp
          </button>
        </div>
      </div>

      {/* Report area - this is ALL that prints */}
      <div id="site-inspection-print-area" className="bg-white rounded-xl border border-gray-200 p-6">
        {/* Report header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-navy flex items-center gap-2">
              <FaClipboardCheck className="text-green-500" /> Site Inspection Report
            </h2>
            <p className="text-sm text-gray-500 mt-1">One Purpose Logistics (Pty) Ltd</p>
          </div>
          {isSubmitted && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <FaCheckCircle className="text-green-600" />
              <span className="text-green-700 font-medium">Site Inspection Complete</span>
            </div>
          )}
        </div>

        {/* Quote details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-xs text-gray-500">Quote Reference</p>
            <p className="font-bold text-navy">{quote.quote_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Customer</p>
            <p className="font-semibold text-navy">{quote.customer_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Type</p>
            <p className="font-semibold text-navy">{quote.quote_type || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Submitted</p>
            <p className="font-semibold text-navy">{formatDate(inspection.submitted_at)}</p>
          </div>
        </div>

        {(quote.origin || quote.destination || quote.description) && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            {quote.origin && <p className="text-sm text-gray-700"><strong>From:</strong> {quote.origin}</p>}
            {quote.destination && <p className="text-sm text-gray-700"><strong>To:</strong> {quote.destination}</p>}
            {quote.description && <p className="text-sm text-gray-700 mt-1"><strong>Details:</strong> {quote.description}</p>}
          </div>
        )}

        {!isSubmitted ? (
          <div className="text-center py-10">
            <FaSpinner className="animate-spin text-4xl text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">This inspection has not been completed yet.</p>
          </div>
        ) : (
          <>
            {/* Answers */}
            <h3 className="font-semibold text-navy mb-3">Inspection Answers</h3>
            <div className="space-y-0 overflow-hidden rounded-lg border border-gray-100 mb-6">
              {questions.map((q, idx) => (
                <div key={q.key} className={`grid grid-cols-1 md:grid-cols-2 gap-2 p-3 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                  <p className="text-sm text-gray-600 font-medium">{q.label}</p>
                  <p className="text-sm text-navy font-semibold">{answers[q.key] || '—'}</p>
                </div>
              ))}
            </div>

            {/* Custom questions */}
            {inspection.custom_questions && inspection.custom_questions.length > 0 && (
              <>
                <h3 className="font-semibold text-navy mb-3">Custom Questions</h3>
                <div className="space-y-0 overflow-hidden rounded-lg border border-orange-100 mb-6">
                  {inspection.custom_questions.map((cq, idx) => (
                    <div key={cq.key || idx} className={`grid grid-cols-1 md:grid-cols-2 gap-2 p-3 ${idx % 2 === 0 ? 'bg-orange-50' : 'bg-white'}`}>
                      <p className="text-sm text-gray-600 font-medium">{cq.label}</p>
                      <p className="text-sm text-navy font-semibold">{answers[cq.key] || '—'}</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Inspector report data */}
            {inspection.inspector_notes && Object.keys(inspection.inspector_notes).length > 0 && (
              <>
                <h3 className="font-semibold text-navy mb-3">🧾 Inspector Report</h3>
                <div className="space-y-2 p-4 bg-purple-50 rounded-lg border border-purple-100 mb-6">
                  {inspection.inspector_notes.assessor && (
                    <p className="text-sm"><span className="text-gray-500 font-medium">Assessor:</span> {inspection.inspector_notes.assessor}</p>
                  )}
                  {inspection.inspector_notes.surveyor_rate && (
                    <p className="text-sm"><span className="text-gray-500 font-medium">Surveyor Rate:</span> R {inspection.inspector_notes.surveyor_rate}</p>
                  )}
                  {inspection.inspector_notes.scope && (
                    <p className="text-sm"><span className="text-gray-500 font-medium">Scope:</span> {inspection.inspector_notes.scope}</p>
                  )}
                  {inspection.inspector_notes.notes && (
                    <p className="text-sm"><span className="text-gray-500 font-medium">Notes:</span> {inspection.inspector_notes.notes}</p>
                  )}
                  {inspection.inspector_notes.safety && Object.keys(inspection.inspector_notes.safety).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Safety / Docs:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(inspection.inspector_notes.safety).filter(([, v]) => v).map(([k]) => (
                          <span key={k} className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5">{k}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {inspection.inspector_notes.works && Object.keys(inspection.inspector_notes.works).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Works:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(inspection.inspector_notes.works).filter(([, v]) => v).map(([k]) => (
                          <span key={k} className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5">{k}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {inspection.inspector_notes.items && inspection.inspector_notes.items.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Items:</p>
                      <div className="overflow-x-auto mt-1">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-white">
                              <th className="text-left px-2 py-1">QTY</th>
                              <th className="text-left px-2 py-1">Description</th>
                              <th className="text-left px-2 py-1">L</th>
                              <th className="text-left px-2 py-1">W</th>
                              <th className="text-left px-2 py-1">H</th>
                              <th className="text-left px-2 py-1">DIA</th>
                              <th className="text-left px-2 py-1">WEIGHT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inspection.inspector_notes.items.filter(i => i.description).map((it, idx) => (
                              <tr key={idx} className="border-t border-gray-100">
                                <td className="px-2 py-0.5">{it.qty || ''}</td>
                                <td className="px-2 py-0.5">{it.description}</td>
                                <td className="px-2 py-0.5">{it.l || ''}</td>
                                <td className="px-2 py-0.5">{it.w || ''}</td>
                                <td className="px-2 py-0.5">{it.h || ''}</td>
                                <td className="px-2 py-0.5">{it.dia || ''}</td>
                                <td className="px-2 py-0.5">{it.weight || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Uploaded files */}
            {inspection.file_urls && inspection.file_urls.length > 0 && (
              <>
                <h3 className="font-semibold text-navy mb-3">📎 Uploaded Files</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {inspection.file_urls.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline bg-blue-50 px-3 py-2 rounded-lg border border-blue-100 flex items-center gap-2"
                    >
                      <FaFilePdf size={12} /> File {idx + 1}
                    </a>
                  ))}
                </div>
              </>
            )}

            {/* Print footer - only visible when printing */}
            <div className="mt-8 pt-4 border-t border-gray-200 text-center text-xs text-gray-400 hidden print:block">
              <p>One Purpose Logistics | info@onepurposelogistics.com | +27 63 598 5855</p>
              <p className="mt-1">Report generated {new Date().toLocaleString('en-ZA')}</p>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default SiteInspectionDetail;